<template>
<div class="ai-suggest-view rabiitrip-screen">
    <AppHeader />

    <main class="view-content rabiitrip-shell" tabindex="-1">
      <div class="container">
        <!-- ページヘッダー -->
        <div class="page-header rabii-hero">
          <router-link to="/destination-boards" class="back-link">
            <span>← 一覧に戻る</span>
          </router-link>
          <h1 class="page-title">
            <IconSvg name="earth-americas-solid-full" size="md" /> AI旅行先相談・提案
          </h1>
          <p class="page-subtitle">AIがあなたの要望にぴったりの旅行先を5件提案します</p>
        </div>

        <!-- 入力フォーム -->
        <div class="card form-card rabii-card" v-if="!proposals.length && !isLoading">
          <div class="form-header">
            <h2>旅行条件を入力</h2>
            <p>行きたい雰囲気や条件を入力してください。AIが最適な旅行先を5件提案します。</p>
          </div>

          <form @submit.prevent="handlePropose" class="suggest-form">
            <div class="form-grid">
              <!-- 出発地点 -->
              <div class="form-group">
                <label class="form-label" for="departurePlace">出発地</label>
                <input
                  v-model="form.departurePlace"
                  type="text"
                  id="departurePlace"
                  class="form-input"
                  placeholder="例: 東京、大阪、福岡"
                />
              </div>

              <!-- 旅行タイプ -->
              <div class="form-group">
                <label class="form-label" for="tripType">旅行タイプ</label>
                <select
                  v-model="form.tripType"
                  id="tripType"
                  class="form-input"
                  :aria-invalid="tripTypeError ? 'true' : 'false'"
                  aria-describedby="tripType-error"
                  @change="tripTypeError = ''"
                >
                  <option value="">指定なし</option>
                  <option value="domestic">国内旅行</option>
                  <option value="international">海外旅行</option>
                </select>
                <p
                  v-if="tripTypeError"
                  id="tripType-error"
                  class="form-validation-message"
                  role="alert"
                >
                  {{ tripTypeError }}
                </p>
              </div>

              <!-- 同行者 -->
              <div class="form-group">
                <label class="form-label" for="companion">同行者</label>
                <select v-model="form.companion" id="companion" class="form-input">
                  <option value="">指定なし</option>
                  <option value="一人旅">一人旅</option>
                  <option value="友人同士">友人同士</option>
                  <option value="カップル・夫婦">カップル・夫婦</option>
                  <option value="家族旅行（子どもあり）">家族旅行（子どもあり）</option>
                  <option value="家族旅行（大人中心・三世代）">家族旅行（大人中心・三世代）</option>
                  <option value="社員旅行・グループ">社員旅行・グループ</option>
                </select>
              </div>

              <!-- 希望の時期 -->
              <div class="form-group">
                <label class="form-label">希望の季節</label>
                <div class="chip-group">
                  <button
                    v-for="s in seasons"
                    :key="s.value"
                    type="button"
                    class="chip-btn"
                    :class="{ active: form.season === s.value }"
                    :aria-pressed="form.season === s.value ? 'true' : 'false'"
                    @click="form.season = s.value"
                  >
                    {{ s.label }}
                  </button>
                </div>
              </div>

              <!-- 期間 -->
              <div class="form-group">
                <label class="form-label" for="days">旅行日数（日）</label>
                <input
                  v-model.number="form.days"
                  type="number"
                  id="days"
                  class="form-input"
                  min="1"
                  max="90"
                  placeholder="例: 5"
                />
              </div>

              <!-- 予算 -->
              <div class="form-group">
                <label class="form-label" for="budget">ご予算（1人あたり円）</label>
                <input
                  v-model.number="form.budget"
                  type="number"
                  id="budget"
                  class="form-input"
                  min="0"
                  step="10000"
                  placeholder="例: 150000"
                />
              </div>

              <!-- 人数 -->
              <div class="form-group">
                <label class="form-label" for="memberCount">旅行人数（人）</label>
                <input
                  v-model.number="form.memberCount"
                  type="number"
                  id="memberCount"
                  class="form-input"
                  min="1"
                  placeholder="例: 2"
                />
              </div>

              <!-- 既訪地除外 -->
              <div class="form-group checkbox-group" style="flex-direction: row; align-items: center; gap: 0.5rem; align-self: center; margin-top: 1.5rem;">
                <input
                  v-model="form.excludeVisited"
                  type="checkbox"
                  id="excludeVisited"
                  class="form-checkbox"
                />
                <label class="form-label" for="excludeVisited" style="margin-bottom: 0; cursor: pointer;">作成者が訪れたことのある場所を除外する</label>
              </div>
            </div>

            <div class="preference-flow" aria-label="旅行先検討の希望">
              <div class="preference-step">
                <div class="step-heading">
                  <div>
                    <strong>行先</strong>
                    <small>メンバーが希望しない旅行先を候補から除外する</small>
                  </div>
                </div>
                <div class="form-group compact-group">
                  <label class="form-label" for="excludeVisitedPlaces">メンバーが除外したい旅行先</label>
                  <input
                    v-model="form.excludeVisitedPlaces"
                    type="text"
                    id="excludeVisitedPlaces"
                    class="form-input"
                    placeholder="例: 台北、韓国、ハワイ。同行者が行った場所も入力できます"
                  />
                </div>
              </div>
            </div>
            <!-- キーワード -->
            <div class="form-group keyword-group">
              <label class="form-label">旅行のテーマ・キーワード（複数選択可）</label>
              <div class="chip-group">
                <button
                  v-for="kw in availableKeywords"
                  :key="kw"
                  type="button"
                  class="chip-btn"
                  :class="{ active: selectedKeywords.includes(kw) }"
                  :aria-pressed="selectedKeywords.includes(kw) ? 'true' : 'false'"
                  @click="toggleKeyword(kw)"
                >
                  {{ kw }}
                </button>
              </div>
              <input
                v-model="customKeyword"
                type="text"
                class="form-input custom-kw-input"
                :placeholder="isMobile ? 'キーワード（例: 世界遺産）' : 'その他のキーワードを自由に入力（例: 世界遺産、ダイビング）'"
              />
            </div>

            <div class="form-actions">
              <button type="submit" class="btn-propose">
                AIに提案してもらう
              </button>
            </div>
          </form>
        </div>

        <!-- ローディング画面 -->
        <div v-if="isLoading" class="loading-container card rabii-card">
          <div class="loader-animation">
            <div class="pulse-ring"></div>
            <IconSvg name="earth-americas-solid-full" size="xl" class="spin-icon" />
          </div>
          <h3>AIが旅行先を提案中...</h3>
          <p>あなたにぴったりの5つの旅行先を選んでいます。10〜15秒ほどお待ちください。</p>
        </div>

        <!-- 提案結果表示 -->
        <div v-if="proposals.length > 0 && !isLoading" class="results-container rabii-section">
          <div class="results-header rabii-hero">
            <h2>
              <svg class="icon-inline sparkles-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="currentColor" style="vertical-align: middle; margin-right: 0.2em;">
                <path d="M9 4.5L10.5 7.5L13.5 9L10.5 10.5L9 13.5L7.5 10.5L4.5 9L7.5 7.5L9 4.5M19 12L20 14L22 15L20 16L19 18L18 16L16 15L18 14L19 12M19 3.5L19.75 5L21.25 5.75L19.75 6.5L19 8L18.25 6.5L16.75 5.75L18.25 5L19 3.5Z" />
              </svg>
              旅行先候補一覧
            </h2>
            <p>入力条件に合う5つの旅行先候補をおすすめします。</p>
          </div>

          <div class="decision-flow-card rabii-card" aria-label="旅行検討の流れ">
            <div class="flow-step active">
              <strong>行先</strong>
              <small>{{ destinationPreferenceSummary }}</small>
            </div>
          </div>

          <div class="proposal-carousel-summary" aria-label="旅行先候補の表示状況">
            <span class="proposal-count">{{ proposals.length }}件の候補</span>
            <div class="proposal-position">
              <button
                type="button"
                class="proposal-nav-button"
                aria-label="前の候補を見る"
                :disabled="currentProposalIndex === 0"
                @click="scrollToProposal(currentProposalIndex - 1)"
              >
                ‹
              </button>
              <span>{{ currentProposalIndex + 1 }} / {{ proposals.length }}</span>
              <button
                type="button"
                class="proposal-nav-button"
                aria-label="次の候補を見る"
                :disabled="currentProposalIndex >= proposals.length - 1"
                @click="scrollToProposal(currentProposalIndex + 1)"
              >
                ›
              </button>
            </div>
          </div>

          <div
            ref="proposalScroller"
            class="proposal-cards"
            aria-label="旅行先候補"
            @scroll.passive="handleProposalScroll"
          >
            <div
              v-for="(prop, index) in proposals.slice(0, 5)"
              :key="index"
              class="proposal-card card rabii-card"
            >
              <div class="proposal-badge">候補 {{ index + 1 }} / {{ proposals.length }}</div>
              <div class="proposal-card-header">
                <h3 class="city-name">{{ prop.cityName }}</h3>
              </div>
              <div
                v-if="recommendationItems(prop).length"
                class="proposal-recommendations"
              >
                <div
                  v-for="item in recommendationItems(prop)"
                  :key="item.type + item.name"
                  class="recommendation-card"
                >
                  <div>
                    <span>{{ item.type }}</span>
                    <strong>{{ item.name }}</strong>
                    <p v-if="item.description">{{ item.description }}</p>
                  </div>
                </div>
              </div>
              <div class="proposal-card-body">
                <div class="prop-detail-item reason-item">
                  <strong>
                    <svg class="icon-inline lightbulb-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 006 8c0 1 .6 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
                      <path d="M9 18h6" />
                      <path d="M10 22h4" />
                    </svg>
                    おすすめの理由
                  </strong>
                  <p>{{ prop.reason }}</p>
                </div>
                <div class="prop-meta-grid">
                  <div class="prop-detail-item">
                    <strong>
                      <svg class="icon-inline weather-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                        <path d="M12 2v2m0 16v2M4.93 4.93l1.41 1.41m11.32 11.32l1.41 1.41M2 12h2m16 0h2M6.34 17.66l-1.41 1.41m11.32-11.32l-1.41 1.41" />
                        <circle cx="12" cy="12" r="4" fill="currentColor" />
                      </svg>
                      ベストシーズン
                    </strong>
                    <p>{{ prop.bestSeason }}</p>
                  </div>
                  <div class="prop-detail-item">
                    <strong>
                      <svg class="icon-inline yen-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                        <path d="M6 3l6 8 6-8" />
                        <path d="M12 11v10" />
                        <path d="M8 15h8" />
                        <path d="M8 11h8" />
                      </svg>
                      概算予算
                    </strong>
                    <p>約 {{ formatCurrency(prop.estimatedBudget) }}</p>
                    <div v-if="prop.budgetBreakdown" class="budget-breakdown-list">
                      <span class="breakdown-tag">交通: {{ formatCurrency(prop.budgetBreakdown.transit) }}</span>
                      <span class="breakdown-tag">宿泊: {{ formatCurrency(prop.budgetBreakdown.accommodation) }}</span>
                      <span class="breakdown-tag">食費: {{ formatCurrency(prop.budgetBreakdown.food) }}</span>
                      <span class="breakdown-tag">他: {{ formatCurrency(prop.budgetBreakdown.other) }}</span>
                    </div>
                  </div>
                  <div class="prop-detail-item" v-if="prop.transitRoute">
                    <strong>
                      交通手段・ルート
                    </strong>
                    <p>{{ prop.transitRoute }}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div class="proposal-carousel-dots" aria-label="旅行先候補のページ">
            <button
              v-for="(_, index) in proposals"
              :key="`proposal-dot-${index}`"
              type="button"
              class="proposal-dot"
              :class="{ active: index === currentProposalIndex }"
              :aria-label="`候補 ${index + 1} を見る`"
              :aria-current="index === currentProposalIndex ? 'true' : undefined"
              @click="scrollToProposal(index)"
            ></button>
          </div>

          <!-- 検討ボード作成エリア -->
          <div class="finalize-card card rabii-card">
            <h2>
              <svg class="icon-inline clipboard-icon" viewBox="0 0 24 24" width="1.2em" height="1.2em" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="vertical-align: middle; margin-right: 0.2em;">
                <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
                <rect x="8" y="2" width="8" height="4" rx="1" ry="1" fill="currentColor" fill-opacity="0.2" />
              </svg>
              この提案で旅行先の検討を始めますか？
            </h2>
            <p>
              この5つの旅行先を初期候補地として登録した「旅行先検討ボード」を作成し、
              メンバーを招待して相談を始められます。
            </p>

            <form @submit.prevent="handleCreateBoard" class="finalize-form">
              <div class="form-group">
                <label class="form-label">検討ボードのタイトル</label>
                <input
                  v-model="boardTitle"
                  type="text"
                  class="form-input"
                  placeholder="例: 夏休み旅行の行き先相談"
                  maxlength="255"
                  required
                />
              </div>
              <div class="finalize-actions">
                <button
                  type="button"
                  class="btn-secondary"
                  @click="resetProposals"
                >
                  条件変更
                </button>
                <button
                  type="submit"
                  class="btn-primary"
                  id="btn-create-board-from-ai"
                  data-track="ai-create-board"
                  :disabled="isCreatingBoard"
                >
                  <span v-if="isCreatingBoard" class="spinner-small"></span>
                  作成
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from "vue";
import { useRouter } from "vue-router";
import AppHeader from "../components/AppHeader.vue";
import IconSvg from "../components/IconSvg.vue";
import { useDestinationBoardStore } from "@/store/destination-board-store.js";

export default {
  name: "AiDestinationSuggestView",
  components: {
    AppHeader,
    IconSvg,
  },
  setup() {
    const router = useRouter();
    const boardStore = useDestinationBoardStore();

    const isMobile = ref(false);
    const checkMobile = () => {
      isMobile.value = window.innerWidth < 768;
    };

    onMounted(() => {
      checkMobile();
      window.addEventListener("resize", checkMobile);
    });

    onUnmounted(() => {
      window.removeEventListener("resize", checkMobile);
    });

    const isLoading = ref(false);
    const isCreatingBoard = ref(false);
    const proposals = ref([]);
    const proposalScroller = ref(null);
    const currentProposalIndex = ref(0);
    const boardTitle = ref("");
    const tripTypeError = ref("");
    const memberCountEdited = ref(false);
    const isApplyingCompanionDefault = ref(false);

    const seasons = [
      { label: "春 (3〜5月)", value: "春" },
      { label: "夏 (6〜8月)", value: "夏" },
      { label: "秋 (9〜11月)", value: "秋" },
      { label: "冬 (12〜2月)", value: "冬" },
      { label: "指定なし", value: "指定なし" },
    ];

    const availableKeywords = [
      "歴史・文化",
      "リゾート・のんびり",
      "ショッピング・都会",
      "グルメ",
      "自然・アクティビティ",
      "コスパ重視",
    ];

    const selectedKeywords = ref([]);
    const customKeyword = ref("");

    const form = reactive({
      season: "指定なし",
      days: null,
      budget: null,
      memberCount: 1,
      departurePlace: "",
      tripType: "",
      companion: "",
      excludeVisited: false,
      excludeVisitedPlaces: "",
    });

    const getCompanionDefaultCount = (companion = "") => {
      const value = String(companion).toLowerCase();
      if (!value) return null;
      if (value.includes("solo") || value.includes("一人") || value.includes("ひとり")) return 1;
      if (value.includes("couple") || value.includes("カップル") || value.includes("夫婦")) return 2;
      if (value.includes("friend") || value.includes("友")) return 2;
      if (value.includes("child") || value.includes("子")) return 4;
      if (value.includes("adult") || value.includes("multigen") || value.includes("三世代") || value.includes("家族")) return 4;
      if (value.includes("group") || value.includes("社員") || value.includes("グループ")) return 5;
      return null;
    };

    watch(
      () => form.companion,
      (companion) => {
        const defaultCount = getCompanionDefaultCount(companion);
        if (!defaultCount) return;
        isApplyingCompanionDefault.value = true;
        form.memberCount = defaultCount;
        memberCountEdited.value = false;
      }
    );

    watch(
      () => form.memberCount,
      () => {
        if (isApplyingCompanionDefault.value) {
          isApplyingCompanionDefault.value = false;
          return;
        }
        memberCountEdited.value = true;
      }
    );

    const toggleKeyword = (kw) => {
      const idx = selectedKeywords.value.indexOf(kw);
      if (idx >= 0) {
        selectedKeywords.value.splice(idx, 1);
      } else {
        selectedKeywords.value.push(kw);
      }
    };

    const trimValue = (value) => String(value || "").trim();

    const destinationPreferenceSummary = computed(() => {
      const pieces = [];
      if (form.excludeVisited) pieces.push("未訪問優先");
      if (trimValue(form.excludeVisitedPlaces)) pieces.push(`除外: ${trimValue(form.excludeVisitedPlaces)}`);
      return pieces.length ? pieces.join(" / ") : "希望に合う旅行先を見比べる";
    });

    const combinedKeywords = computed(() => {
      const list = [...selectedKeywords.value];
      if (customKeyword.value.trim()) {
        list.push(customKeyword.value.trim());
      }
      return list.join(", ");
    });

    const handlePropose = async () => {
      if (!form.tripType) {
        tripTypeError.value = "旅行タイプを選択してください。";
        return;
      }
      tripTypeError.value = "";
      isLoading.value = true;
      try {
        const params = {
          season: form.season === "指定なし" ? null : form.season,
          days: form.days || null,
          budget: form.budget || null,
          memberCount: form.memberCount || 1,
          keywords: combinedKeywords.value || null,
          departurePlace: form.departurePlace || null,
          tripType: form.tripType,
          companion: form.companion || null,
          excludeVisited: form.excludeVisited,
          excludeVisitedPlaces: form.excludeVisitedPlaces || null,
        };
        const result = await boardStore.proposeAiDestinations(params);
        if (!Array.isArray(result) || result.length !== 5) {
          alert("旅行先候補を正しく取得できませんでした。時間をおいて再度お試しください。");
          return;
        }
        proposals.value = result;
        currentProposalIndex.value = 0;

        // GA4イベント送信
        if (window.gtag) {
          window.gtag("event", "ai_proposal_request", {
            season: params.season,
            days: params.days,
            budget: params.budget,
            member_count: params.memberCount,
            keywords: params.keywords,
            departure_place: params.departurePlace,
            trip_type: params.tripType,
            companion: params.companion,
            exclude_visited: params.excludeVisited,
            exclude_visited_places: params.excludeVisitedPlaces,
            status: "success"
          });
        }

        // デフォルトのボードタイトルを自動生成
        let kwPart = selectedKeywords.value[0] || customKeyword.value || "AI提案";
        let seasonPart = form.season !== "指定なし" ? form.season : "";
        boardTitle.value = `${seasonPart}の${kwPart}旅行計画`;
      } catch (err) {
        alert("AI都市提案の取得に失敗しました。時間をおいて再度お試しください。");
        console.error(err);
      } finally {
        isLoading.value = false;
      }
    };

    const handleCreateBoard = async () => {
      if (!boardTitle.value.trim()) return;
      if (!form.tripType) {
        alert("旅行タイプを選択してください。");
        return;
      }
      if (proposals.value.length !== 5) {
        alert("旅行先候補を5件そろえてからボードを作成してください。");
        return;
      }
      isCreatingBoard.value = true;
      try {
        const params = {
          boardTitle: boardTitle.value.trim(),
          proposals: proposals.value,
          estimatedDays: form.days,
          tripType: form.tripType,
        };
        const result = await boardStore.createBoardFromAiProposal(params);
        const boardId = result?.BOARD_ID || result?.board_id || result?.boardId;
        if (boardId) {
          if (window.gtag) {
            window.gtag("event", "ai_create_board", {
              board_id: boardId,
              board_title: params.boardTitle,
              proposal_count: params.proposals ? params.proposals.length : 0,
              status: "success"
            });
          }
          router.push(`/destination-board/${boardId}`);
        } else {
          alert("ボードは作成されましたが、詳細画面への遷移に失敗しました。");
          router.push("/mypage");
        }
      } catch (err) {
        alert("検討ボードの作成に失敗しました。");
        console.error(err);
      } finally {
        isCreatingBoard.value = false;
      }
    };

    const resetProposals = () => {
      proposals.value = [];
      currentProposalIndex.value = 0;
      boardTitle.value = "";
    };

    const handleProposalScroll = () => {
      const scroller = proposalScroller.value;
      if (!scroller || !scroller.children.length) return;
      const firstCard = scroller.children[0];
      const styles = window.getComputedStyle(scroller);
      const gap = parseFloat(styles.columnGap || styles.gap || 0) || 0;
      const step = firstCard.getBoundingClientRect().width + gap;
      if (!step) return;
      const nextIndex = Math.round(scroller.scrollLeft / step);
      currentProposalIndex.value = Math.max(
        0,
        Math.min(nextIndex, proposals.value.length - 1)
      );
    };

    const scrollToProposal = (index) => {
      const scroller = proposalScroller.value;
      if (!scroller || !scroller.children.length) return;
      const targetIndex = Math.max(0, Math.min(index, proposals.value.length - 1));
      const target = scroller.children[targetIndex];
      if (!target) return;
      target.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "start",
      });
      currentProposalIndex.value = targetIndex;
    };

    const formatCurrency = (val) => {
      if (!val) return "未定";
      return new Intl.NumberFormat("ja-JP", {
        style: "currency",
        currency: "JPY",
        maximumFractionDigits: 0,
      }).format(val);
    };

    const normalizeRecommendation = (item, type) => {
      if (!item) return null;
      if (typeof item === "string") {
        return { type, name: item, description: "", imageUrl: "", referenceUrl: "" };
      }
      return {
        type,
        name: item.name || item.title || "",
        description: item.description || "",
        imageUrl: item.imageUrl || item.image_url || "",
        referenceUrl: item.referenceUrl || item.reference_url || "",
      };
    };

    const recommendationItems = (proposal) => {
      const spots = Array.isArray(proposal?.spotRecommendations)
        ? proposal.spotRecommendations
        : Array.isArray(proposal?.recommendedSpots)
          ? proposal.recommendedSpots
          : Array.isArray(proposal?.recommendSpots)
            ? proposal.recommendSpots
            : [];
      const foods = Array.isArray(proposal?.foodRecommendations)
        ? proposal.foodRecommendations
        : [];
      return [
        ...spots.map((item) => normalizeRecommendation(item, "観光")),
        ...foods.map((item) => normalizeRecommendation(item, "グルメ")),
      ].filter((item) => item && item.name);
    };

    return {
      isLoading,
      isCreatingBoard,
      proposals,
      proposalScroller,
      currentProposalIndex,
      boardTitle,
      tripTypeError,
      seasons,
      availableKeywords,
      selectedKeywords,
      customKeyword,
      form,
      toggleKeyword,
      destinationPreferenceSummary,
      handlePropose,
      handleCreateBoard,
      resetProposals,
      handleProposalScroll,
      scrollToProposal,
      formatCurrency,
      recommendationItems,
      isMobile,
      memberCountEdited,
    };
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700&display=swap');

.ai-suggest-view {
  min-height: 100vh;
  background-color: var(--color-background);
  font-family: "Space Grotesk", "M PLUS Rounded 1c", -apple-system, BlinkMacSystemFont, "Noto Sans JP", sans-serif;
  --primary-color: #b4cf9e;
  --primary-light: #c8dbc0;
  --primary-dark: #8ca87a;
  --primary-rgb: 180, 207, 158;
}

.view-content {
  padding: 2rem 1rem;
}

.back-link {
  display: inline-block;
  margin-bottom: 1rem;
  color: #64748b;
  text-decoration: none;
  font-weight: 600;
  transition: color 0.2s;
}

.back-link:hover {
  color: #1e293b;
}

.page-header {
  margin-bottom: 2rem;
}

.page-title {
  font-size: 1.875rem;
  font-weight: 800;
  color: #1e293b;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-subtitle {
  font-size: 1rem;
  color: #64748b;
  margin-top: 0.5rem;
}

/* カード共通 */
.card {
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-radius: 20px;
  padding: 2rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.03), inset 0 1px 0 rgba(255, 255, 255, 0.6);
  border: 1px solid rgba(226, 232, 240, 0.8);
  margin-bottom: 2rem;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

/* フォームヘッダー */
.form-header {
  margin-bottom: 2rem;
  border-bottom: 1px solid #f1f5f9;
  padding-bottom: 1rem;
}

.form-header h2 {
  font-size: 1.25rem;
  font-weight: 700;
  color: #1e293b;
}

.form-header p {
  font-size: 0.875rem;
  color: #64748b;
  margin-top: 0.25rem;
}

/* フォームグリッド */
.form-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 1.5rem;
}

@media (min-width: 768px) {
  .form-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 700;
  color: #475569;
}

.form-input {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background-color: var(--color-surface);
  transition: all 0.2s;
}

.form-input:focus {
  outline: none;
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.15);
}

/* チップグループ */
.chip-group {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.chip-btn {
  padding: 0.5rem 1rem;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  background: var(--color-surface);
  color: #475569;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 44px;
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.chip-btn:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.chip-btn.active {
  background: var(--primary-color);
  color: white;
  border-color: var(--primary-color);
}

.preference-flow {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 1rem;
  margin: 1rem 0 1.5rem;
}

.preference-step {
  min-width: 0;
  padding: 1rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.step-heading {
  display: flex;
  gap: 0.7rem;
  align-items: flex-start;
  margin-bottom: 0.75rem;
}

.step-heading strong,
.step-heading small {
  display: block;
}

.step-heading strong {
  color: #1e293b;
  font-size: 0.95rem;
}

.step-heading small {
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.45;
}

.compact-group + .compact-group {
  margin-top: 0.75rem;
}
.keyword-group {
  margin-top: 1.5rem;
}

.custom-kw-input {
  margin-top: 0.75rem;
}

.form-actions {
  margin-top: 2rem;
  display: flex;
  justify-content: center;
}

.btn-propose {
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--color-primary-light) 100%);
  color: white;
  border: none;
  padding: 1rem 3rem;
  font-size: 1.125rem;
  font-weight: 700;
  border-radius: 30px;
  cursor: pointer;
  box-shadow: 0 10px 20px rgba(var(--primary-rgb), 0.25);
  transition: all 0.3s;
  white-space: nowrap;
  min-width: 7.5rem;
}

.btn-propose:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 25px rgba(var(--primary-rgb), 0.35);
}

/* ローディング */
.loading-container {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 4rem 2rem;
}

.loader-animation {
  position: relative;
  width: 80px;
  height: 80px;
  margin-bottom: 2rem;
  display: grid;
  place-items: center;
}

.spin-icon {
  animation: spin 10s linear infinite;
  color: var(--primary-color);
  z-index: 2;
}

.pulse-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: rgba(var(--primary-rgb), 0.15);
  animation: pulse 2s ease-out infinite;
  z-index: 1;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

@keyframes pulse {
  0% { transform: scale(0.8); opacity: 0.5; }
  100% { transform: scale(1.5); opacity: 0; }
}

/* 結果表示 */
.results-header {
  margin-bottom: 2rem;
  text-align: center;
}

.results-header h2 {
  font-size: 1.75rem;
  color: #1e293b;
  font-weight: 800;
}

.results-header p {
  color: #64748b;
  margin-top: 0.5rem;
  font-size: 1rem;
}

.decision-flow-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.75rem;
  margin-bottom: 1.5rem;
  padding: 1rem;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.flow-step {
  min-width: 0;
  padding: 0.75rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.flow-step.active {
  border-color: rgba(var(--primary-rgb), 0.45);
  background: rgba(var(--primary-rgb), 0.08);
}

.flow-step strong,
.flow-step small {
  display: block;
}

.flow-step strong {
  color: #1e293b;
  font-size: 0.95rem;
}

.flow-step small {
  margin-top: 0.25rem;
  color: #64748b;
  font-size: 0.78rem;
  line-height: 1.45;
}

.proposal-cards {
  display: grid;
  grid-template-columns: 1fr;
  gap: 1.5rem;
  margin-bottom: 2.5rem;
}

.proposal-carousel-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin: 0 0 0.85rem;
}

.proposal-count {
  display: inline-flex;
  align-items: center;
  min-height: 2rem;
  padding: 0.35rem 0.75rem;
  border: 1px solid rgba(var(--primary-rgb), 0.28);
  border-radius: 999px;
  background: rgba(var(--primary-rgb), 0.09);
  color: #36546b;
  font-size: 0.8rem;
  font-weight: 800;
}

.proposal-position {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: #475569;
  font-size: 0.82rem;
  font-weight: 800;
}

.proposal-nav-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border: 1px solid #dbe5ee;
  border-radius: 50%;
  background: #fff;
  color: #36546b;
  cursor: pointer;
  font: inherit;
  font-size: 1.25rem;
  font-weight: 800;
  line-height: 1;
  transition: border-color 0.2s ease, color 0.2s ease, transform 0.2s ease;
}

.proposal-nav-button:hover:not(:disabled) {
  border-color: rgba(var(--primary-rgb), 0.55);
  color: #1e293b;
  transform: translateY(-1px);
}

.proposal-nav-button:disabled {
  opacity: 0.38;
  cursor: default;
}

.proposal-carousel-dots {
  display: flex;
  justify-content: center;
  gap: 0.45rem;
  margin: -1.35rem 0 2.5rem;
}

.proposal-dot {
  position: relative;
  width: 44px;
  height: 44px;
  padding: 0;
  border: 0;
  background: transparent;
  cursor: pointer;
}

.proposal-dot::before {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0.55rem;
  height: 0.55rem;
  border-radius: 50%;
  background: #cbd5e1;
  transform: translate(-50%, -50%);
  transition: background-color 0.2s ease, width 0.2s ease;
}

.proposal-dot.active::before {
  width: 1.4rem;
  border-radius: 999px;
  background: #4f708a;
}

.form-validation-message {
  margin: 0.35rem 0 0;
  color: #b42318;
  font-size: 0.875rem;
  font-weight: 700;
}

@media (min-width: 1024px) {
  .proposal-cards {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }

  .proposal-carousel-dots {
    display: none;
  }
}

.proposal-card {
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  border-top: 4px solid var(--primary-color);
  transition: all 0.3s;
}

.proposal-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.08);
}

.proposal-badge {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background-color: #f1f5f9;
  color: var(--primary-color);
  font-size: 0.75rem;
  font-weight: 800;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
}

.proposal-card-header {
  margin-bottom: 1.5rem;
}

.proposal-recommendations {
  display: grid;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.recommendation-card {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 0.55rem;
  align-items: center;
  padding: 0.6rem 0.7rem;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
}

.recommendation-card span {
  color: #b4cf9e;
  font-size: 0.68rem;
  font-weight: 800;
}

.recommendation-card strong {
  display: block;
  overflow: hidden;
  color: #1e293b;
  font-size: 0.82rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  min-width: 7.5rem;
}

.recommendation-card p {
  display: -webkit-box;
  margin: 0.1rem 0 0;
  overflow: hidden;
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.35;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
}

.city-name {
  font-size: 1.5rem;
  font-weight: 800;
  color: #1e293b;
}

.proposal-card-body {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.prop-detail-item {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.prop-detail-item strong {
  font-size: 0.8125rem;
  color: #64748b;
}

.prop-detail-item p {
  font-size: 0.9375rem;
  color: #334155;
  line-height: 1.5;
  margin: 0;
}

.reason-item p {
  background: #f8fafc;
  padding: 0.75rem;
  border-radius: 8px;
  border-left: 3px solid #cbd5e1;
}

.prop-meta-grid {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: auto;
  border-top: 1px solid #f1f5f9;
  padding-top: 1rem;
}

@media (max-width: 767px) {
  .decision-flow-card,
  .preference-flow {
    grid-template-columns: 1fr;
  }

  .btn-propose {
    width: 100%;
    padding: 0.95rem 1rem;
    font-size: 1rem;
  }

  .proposal-cards {
    display: flex;
    gap: 1rem;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    padding: 0 1.75rem 0.65rem 1rem;
    margin: 0 -1rem 2rem;
  }

  .proposal-card {
    flex: 0 0 min(82vw, 20rem);
    scroll-snap-align: start;
  }
}

.budget-breakdown-list {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem 0.5rem;
  margin-top: 0.25rem;
}

.breakdown-tag {
  font-size: 0.75rem;
  background-color: #f8fafc;
  color: #64748b;
  padding: 0.125rem 0.375rem;
  border-radius: 4px;
  border: 1px solid #e2e8f0;
}

.itinerary-editor {
  width: 100%;
  min-height: 8rem;
  resize: vertical;
  padding: 0.65rem 0.75rem;
  border: 1px dashed #cbd5e1;
  border-radius: 8px;
  background-color: #f8fafc;
  color: #334155;
  font: inherit;
  font-size: 0.875rem;
  line-height: 1.5;
}

.itinerary-editor:focus {
  outline: none;
  border-color: var(--primary-color);
  background: var(--color-surface);
  box-shadow: 0 0 0 3px rgba(var(--primary-rgb), 0.15);
}

.itinerary-help {
  color: #64748b;
  font-size: 0.72rem;
  line-height: 1.4;
}

/* 決定セクション */
.finalize-card {
  text-align: center;
  border-top: 4px solid #4f708a;
}

.finalize-card h2 {
  font-size: 1.5rem;
  font-weight: 800;
  color: #4f708a;
}

.finalize-card p {
  color: #64748b;
  max-width: 600px;
  margin: 0.5rem auto 2rem auto;
  line-height: 1.6;
}

.finalize-form {
  max-width: 500px;
  margin: 0 auto;
  text-align: left;
}

.finalize-actions {
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
}

.btn-secondary {
  flex: 1;
  padding: 0.75rem 1.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  background: var(--color-surface);
  color: #475569;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s;
  text-align: center;
}

.btn-secondary:hover {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.btn-primary {
  flex: 2;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 10px;
  background: linear-gradient(135deg, #35536b 0%, #4f708a 100%);
  color: white;
  font-weight: 700;
  cursor: pointer;
  transition: transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease;
  box-shadow: 0 4px 12px rgba(53, 83, 107, 0.25);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-primary:hover:not(:disabled) {
  background: linear-gradient(135deg, #2b4559 0%, #35536b 100%);
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(53, 83, 107, 0.35);
}

.finalize-actions .btn-secondary:focus,
.finalize-actions .btn-primary:focus,
.finalize-actions .btn-secondary:focus-visible,
.finalize-actions .btn-primary:focus-visible {
  outline: 3px solid #35536b !important;
  outline-offset: 2px;
}

.btn-primary:disabled {
  background: #4f708a;
  background-color: #4f708a;
  box-shadow: none;
  opacity: 0.6;
  cursor: not-allowed;
}

.finalize-actions .btn-secondary,
.finalize-actions .btn-primary {
  font-size: 0.9375rem;
}

.spinner-small {
  width: 1rem;
  height: 1rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  border-top-color: white;
  animation: spin 1s ease-in-out infinite;
}
</style>
